import manifest from "../../../../config/questionnaire-capabilities.json";
import scaleWordingJson from "../../../../config/questionnaire-scale-wording.json";
import {
  MATRIX_MAX_COLUMNS,
  RESEARCH_QUESTION_CEILING,
} from "./question-capabilities";

/**
 * What the builder cannot do, or does only approximately (S4.11), shared with
 * Python (`apps/python-api/models/questionnaire/capabilities.py`, which renders
 * the model-facing meanings). A request part names one as `capability`; the
 * builder words it in the interface's language from the id and these limits.
 * Every registry id: an entry's `stages` narrows only which stage's schema
 * offers it (a revision part never names `question_count`, a first build's may).
 */
export type CapabilityId = keyof typeof manifest.capabilities;

if (manifest.schemaVersion !== "questionnaire-capabilities.v1") {
  throw new Error("Unsupported questionnaire capability registry schema");
}

/** In registry order. */
export const CAPABILITY_IDS = Object.freeze(
  Object.keys(manifest.capabilities) as Array<CapabilityId>,
);

/**
 * The limits a capability's words name, from their owners: the grid column
 * cap, the question ceiling and the survey languages (the scale-wording
 * packs' languages). The
 * builder names the supported rating scales itself, in the reader's language
 * (`ratingScaleUserName`).
 */
export const CAPABILITY_LIMITS = Object.freeze({
  matrixMaxColumns: MATRIX_MAX_COLUMNS,
  researchQuestionCeiling: RESEARCH_QUESTION_CEILING,
  surveyLanguages: Object.freeze(Object.keys(scaleWordingJson)),
});


/**
 * The capabilities that have a nearest supported version (C06-1): the
 * registry states it once as a typed field, which the models read inside the
 * capability's meaning and the builder words for the user by the id. A
 * nearest part of one of these says what was done instead; any other nearest
 * part keeps its capability's words.
 */
type Registry = typeof manifest.capabilities;
export type CapabilityNearestId = {
  [K in keyof Registry]: Registry[K] extends { nearest: string } ? K : never;
}[keyof Registry];

export const CAPABILITY_NEAREST_IDS: ReadonlySet<CapabilityNearestId> = new Set(
  (
    Object.entries(manifest.capabilities) as Array<
      [CapabilityNearestId, { nearest?: string }]
    >
  ).flatMap(([id, entry]) => (entry.nearest ? [id] : [])),
);

/** Whether a capability declares what its nearest supported version does. */
export function capabilityHasNearest(
  capability: CapabilityId,
): capability is CapabilityNearestId {
  return (CAPABILITY_NEAREST_IDS as ReadonlySet<string>).has(capability);
}

/**
 * Whether the upload card takes the file a part needing this capability names
 * (L-B2, the registry's typed `fileOnCard`): another picture of one asset is
 * a swap (BP-01), so its card is offered; a picture of several assets
 * (CT-13's line-up shot) or of items past the study's limit (PR-13's
 * packshots) has nowhere to go, so no card offers it and the part keeps its
 * reason. The models never read it.
 */
export function capabilityFileOnCard(capability: CapabilityId): boolean {
  return (
    (manifest.capabilities[capability] as { fileOnCard?: boolean }).fileOnCard ===
    true
  );
}
